/**
 * EcosystemDirectory
 * 
 * Admin ecosystem management hub with multi-path tree-view,
 * CRUD operations, entity detail panels, and event management.
 * Tree is built from entity_relationships (all types, not just belongs_to).
 */

import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Building2, Calendar, Search, ChevronRight, ChevronDown, Plus, Pencil,
  Loader2, MapPin, Link2, FileText, AlertTriangle,
} from 'lucide-react';
import {
  useEcosystemOrganizations, useEcosystemEventSeries, useEcosystemEvents,
  useAllEntityRelationships,
} from '@/hooks/useEcosystemData';
import {
  ORG_KIND_LABELS, PRIORITY_TIER_LABELS, EVENT_FREQUENCY_LABELS,
  RELATIONSHIP_TYPE_ICONS,
  type OrgKind, type PriorityTier, type EntityRelationshipType,
} from '@/types/ecosystem';
import { AddEditEcosystemEntityDialog } from './AddEditEcosystemEntityDialog';
import { AddEditEventSeriesDialog } from './AddEditEventSeriesDialog';
import { AddEditEventInstanceDialog } from './AddEditEventInstanceDialog';
import { LinkArtifactToEventDialog } from './LinkArtifactToEventDialog';
import { LinkOfferingToEventDialog } from './LinkOfferingToEventDialog';
import { EcosystemEntityDetailPanel } from './EcosystemEntityDetailPanel';

interface EcosystemDirectoryProps {
  defaultTier?: PriorityTier;
}

interface TreeNode {
  id: string;
  display_name: string;
  org_kind: OrgKind | null;
  priority_tier: PriorityTier | null;
  coverage: string | null;
  stable_key: string | null;
  website_url: string | null;
  description: string | null;
  children: TreeNode[];
  relationshipType: EntityRelationshipType;
  relationshipId: string;
  needsReview: boolean;
  isMultiPath: boolean;
  raw: any;
}

/**
 * Build multi-path tree from entity_relationships + training_providers.
 * An entity can appear multiple times under different parents.
 */
function buildMultiPathTree(
  entities: any[],
  relationships: any[],
  showPending: boolean,
): TreeNode[] {
  const entityMap = new Map<string, any>();
  for (const e of entities) entityMap.set(e.id, e);

  // Count how many parent relationships each entity has
  const childPlacementCount = new Map<string, number>();
  for (const r of relationships) {
    if (r.needs_review && !showPending) continue;
    childPlacementCount.set(r.child_id, (childPlacementCount.get(r.child_id) || 0) + 1);
  }

  // Tier 1 gateways (priority_tier = 1 and no parent relationship)
  const entitiesWithParent = new Set<string>();
  for (const r of relationships) {
    if (r.needs_review && !showPending) continue;
    entitiesWithParent.add(r.child_id);
  }

  // Build tree nodes from relationships
  const parentChildMap = new Map<string, TreeNode[]>();

  for (const r of relationships) {
    if (r.needs_review && !showPending) continue;
    const childEntity = entityMap.get(r.child_id);
    if (!childEntity) continue;

    const node: TreeNode = {
      id: childEntity.id,
      display_name: childEntity.display_name,
      org_kind: childEntity.org_kind,
      priority_tier: childEntity.priority_tier,
      coverage: childEntity.coverage,
      stable_key: childEntity.stable_key,
      website_url: childEntity.website_url,
      description: childEntity.description,
      children: [],
      relationshipType: r.relationship_type,
      relationshipId: r.id,
      needsReview: r.needs_review,
      isMultiPath: (childPlacementCount.get(r.child_id) || 0) > 1,
      raw: childEntity,
    };

    if (!parentChildMap.has(r.parent_id)) parentChildMap.set(r.parent_id, []);
    parentChildMap.get(r.parent_id)!.push(node);
  }

  // Recursively attach children
  function attachChildren(node: TreeNode): TreeNode {
    const kids = parentChildMap.get(node.id) || [];
    node.children = kids.map(k => attachChildren({ ...k, children: [] }));
    return node;
  }

  // Find roots: Tier 1 gateways (entities with priority_tier=1 that have no parent in relationships)
  const roots: TreeNode[] = [];
  for (const e of entities) {
    if (e.priority_tier === 1 && !entitiesWithParent.has(e.id)) {
      const root: TreeNode = {
        id: e.id,
        display_name: e.display_name,
        org_kind: e.org_kind,
        priority_tier: e.priority_tier,
        coverage: e.coverage,
        stable_key: e.stable_key,
        website_url: e.website_url,
        description: e.description,
        children: [],
        relationshipType: 'belongs_to',
        relationshipId: '',
        needsReview: false,
        isMultiPath: false,
        raw: e,
      };
      attachChildren(root);
      roots.push(root);
    }
  }

  // Uncategorized: entities that have no parent relationship and are NOT Tier 1 gateways
  const uncategorized: TreeNode[] = [];
  for (const e of entities) {
    if (!entitiesWithParent.has(e.id) && e.priority_tier !== 1) {
      uncategorized.push({
        id: e.id,
        display_name: e.display_name,
        org_kind: e.org_kind,
        priority_tier: e.priority_tier,
        coverage: e.coverage,
        stable_key: e.stable_key,
        website_url: e.website_url,
        description: e.description,
        children: [],
        relationshipType: 'belongs_to',
        relationshipId: '',
        needsReview: false,
        isMultiPath: (childPlacementCount.get(e.id) || 0) > 1,
        raw: e,
      });
    }
  }

  if (uncategorized.length > 0) {
    roots.push({
      id: '__uncategorized__',
      display_name: `Uncategorized (${uncategorized.length})`,
      org_kind: null,
      priority_tier: null,
      coverage: null,
      stable_key: null,
      website_url: null,
      description: null,
      children: uncategorized,
      relationshipType: 'belongs_to',
      relationshipId: '',
      needsReview: false,
      isMultiPath: false,
      raw: null,
    });
  }

  return roots;
}

function TreeNodeRow({
  node,
  depth,
  selectedId,
  onSelect,
  onEdit,
  expandedIds,
  toggleExpand,
}: {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEdit: (entity: any) => void;
  expandedIds: Set<string>;
  toggleExpand: (id: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;
  const relIcon = RELATIONSHIP_TYPE_ICONS[node.relationshipType];

  return (
    <>
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-accent/50 transition-colors ${isSelected ? 'bg-accent' : ''} ${node.needsReview ? 'opacity-60' : ''}`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={() => node.id !== '__uncategorized__' && onSelect(node.id)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }}
            className="shrink-0"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        {relIcon && <span className="text-xs shrink-0" title={node.relationshipType}>{relIcon}</span>}

        <span className="font-medium text-sm truncate flex-1">{node.display_name}</span>

        {node.isMultiPath && (
          <Badge variant="outline" className="text-xs shrink-0 bg-primary/10">×M</Badge>
        )}
        {node.needsReview && (
          <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
        )}
        {node.org_kind && (
          <Badge variant="outline" className="text-xs shrink-0">
            {ORG_KIND_LABELS[node.org_kind] || node.org_kind}
          </Badge>
        )}
        {node.priority_tier && (
          <Badge variant={node.priority_tier === 1 ? 'default' : 'secondary'} className="text-xs shrink-0">
            T{node.priority_tier}
          </Badge>
        )}

        {node.raw && (
          <Button
            variant="ghost" size="icon" className="h-6 w-6 shrink-0"
            onClick={(e) => { e.stopPropagation(); onEdit(node.raw); }}
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
      </div>

      {hasChildren && isExpanded && node.children.map((child, idx) => (
        <TreeNodeRow
          key={`${child.id}-${child.relationshipId || idx}`}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          onEdit={onEdit}
          expandedIds={expandedIds}
          toggleExpand={toggleExpand}
        />
      ))}
    </>
  );
}

export function EcosystemDirectory({ defaultTier }: EcosystemDirectoryProps) {
  const [subTab, setSubTab] = useState<'orgs' | 'events'>('orgs');
  const [tierFilter, setTierFilter] = useState<string>(defaultTier?.toString() || 'all');
  const [kindFilter, setKindFilter] = useState<string>('all');
  const [orgSearch, setOrgSearch] = useState('');
  const [showPending, setShowPending] = useState(false);

  // Dialog states
  const [showEntityDialog, setShowEntityDialog] = useState(false);
  const [editEntity, setEditEntity] = useState<any>(null);
  const [defaultParentId, setDefaultParentId] = useState<string | null>(null);
  const [showSeriesDialog, setShowSeriesDialog] = useState(false);
  const [editSeries, setEditSeries] = useState<any>(null);
  const [showInstanceDialog, setShowInstanceDialog] = useState(false);
  const [showLinkArtifactDialog, setShowLinkArtifactDialog] = useState(false);
  const [showLinkOfferingDialog, setShowLinkOfferingDialog] = useState(false);
  const [defaultSeriesOrganizerId, setDefaultSeriesOrganizerId] = useState<string | undefined>();

  // Selection & expansion
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: orgs, isLoading: orgsLoading } = useEcosystemOrganizations({
    tier: tierFilter !== 'all' ? (Number(tierFilter) as PriorityTier) : undefined,
    org_kind: kindFilter !== 'all' ? (kindFilter as OrgKind) : undefined,
    search: orgSearch || undefined,
  });

  const { data: allRelationships, isLoading: relsLoading } = useAllEntityRelationships();
  const { data: series, isLoading: seriesLoading } = useEcosystemEventSeries();
  const { data: events, isLoading: eventsLoading } = useEcosystemEvents({
    // Admin ecosystem view spans multiple shops in multiple timezones —
    // browser-local "today" is the closest available proxy. Avoid
    // toISOString().slice(0,10) which would silently return UTC tomorrow
    // for any advisor viewing late in the local evening.
    fromDate: format(new Date(), 'yyyy-MM-dd'),
  });

  // Count pending
  const pendingCount = useMemo(() => {
    return (allRelationships || []).filter(r => r.needs_review).length;
  }, [allRelationships]);

  // Build multi-path tree
  const tree = useMemo(
    () => buildMultiPathTree(orgs || [], allRelationships || [], showPending),
    [orgs, allRelationships, showPending],
  );

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedEntity = useMemo(() => {
    if (!selectedEntityId || !orgs) return null;
    return orgs.find((o: any) => o.id === selectedEntityId);
  }, [selectedEntityId, orgs]);

  const handleAddChild = () => {
    setEditEntity(null);
    setDefaultParentId(selectedEntityId);
    setShowEntityDialog(true);
  };

  const handleAddSource = () => {};
  const handleAddOffering = () => {};

  const handleAddSeries = () => {
    setEditSeries(null);
    setDefaultSeriesOrganizerId(selectedEntityId || undefined);
    setShowSeriesDialog(true);
  };

  const isLoading = orgsLoading || relsLoading;

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={(v) => setSubTab(v as 'orgs' | 'events')}>
        <TabsList>
          <TabsTrigger value="orgs" className="gap-2">
            <Building2 className="h-4 w-4" />
            Organizations ({orgs?.length ?? '…'})
          </TabsTrigger>
          <TabsTrigger value="events" className="gap-2">
            <Calendar className="h-4 w-4" />
            Events ({events?.length ?? '…'})
          </TabsTrigger>
        </TabsList>

        {/* ── Organizations Panel ──────────────────────────── */}
        <TabsContent value="orgs" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search organizations…" value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Tiers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="1">Tier 1 — Core</SelectItem>
                <SelectItem value="2">Tier 2 — Active</SelectItem>
                <SelectItem value="3">Tier 3 — Monitored</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {(Object.entries(ORG_KIND_LABELS) as [OrgKind, string][]).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => { setEditEntity(null); setDefaultParentId(null); setShowEntityDialog(true); }} className="gap-2">
              <Plus className="h-4 w-4" /> Add Entity
            </Button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !orgs?.length ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No organizations match the current filters.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Tree View */}
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">Entity Tree</CardTitle>
                  {pendingCount > 0 && (
                    <div className="flex items-center gap-2">
                      <Label htmlFor="showPending" className="text-xs text-muted-foreground">
                        Show pending ({pendingCount})
                      </Label>
                      <Switch id="showPending" checked={showPending} onCheckedChange={setShowPending} />
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-2">
                  <ScrollArea className="h-[550px]">
                    {tree.map(node => (
                      <TreeNodeRow
                        key={node.id}
                        node={node}
                        depth={0}
                        selectedId={selectedEntityId}
                        onSelect={setSelectedEntityId}
                        onEdit={(e) => { setEditEntity(e); setShowEntityDialog(true); }}
                        expandedIds={expandedIds}
                        toggleExpand={toggleExpand}
                      />
                    ))}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Detail Panel */}
              <Card className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    {selectedEntity ? (selectedEntity as any).display_name : 'Select an entity'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[520px] px-2">
                  {selectedEntityId && selectedEntity ? (
                    <EcosystemEntityDetailPanel
                      entityId={selectedEntityId}
                      entityName={(selectedEntity as any).display_name}
                      onAddChild={handleAddChild}
                      onAddSource={handleAddSource}
                      onAddOffering={handleAddOffering}
                      onAddSeries={handleAddSeries}
                      onLinkArtifact={() => setShowLinkArtifactDialog(true)}
                      onLinkOffering={() => setShowLinkOfferingDialog(true)}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                      Click an entity in the tree to view its details, linked sources, offerings, and events.
                    </p>
                  )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ── Events Panel ─────────────────────────────────── */}
        <TabsContent value="events" className="space-y-4">
          {/* Event Series */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Event Series</CardTitle>
                <CardDescription>{series?.length ?? 0} registered series</CardDescription>
              </div>
              <Button size="sm" onClick={() => { setEditSeries(null); setDefaultSeriesOrganizerId(undefined); setShowSeriesDialog(true); }} className="gap-1">
                <Plus className="h-4 w-4" /> Add Series
              </Button>
            </CardHeader>
            <CardContent>
              {seriesLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : !series?.length ? (
                <p className="text-sm text-muted-foreground">No event series found.</p>
              ) : (
                <div className="space-y-2">
                  {series.map((s: any) => (
                    <div key={s.id} className="flex items-center justify-between p-2 rounded-md hover:bg-accent/50">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{s.series_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {EVENT_FREQUENCY_LABELS[s.frequency as keyof typeof EVENT_FREQUENCY_LABELS] || s.frequency}
                        </Badge>
                        {s.organizer?.display_name && (
                          <span className="text-xs text-muted-foreground">by {s.organizer.display_name}</span>
                        )}
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditSeries(s); setShowSeriesDialog(true); }}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Instances */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Upcoming Events</CardTitle>
                <CardDescription>{events?.length ?? 0} upcoming instances</CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowInstanceDialog(true)} className="gap-1">
                <Plus className="h-4 w-4" /> Add Instance
              </Button>
            </CardHeader>
            <CardContent>
              {eventsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : !events?.length ? (
                <p className="text-sm text-muted-foreground">No upcoming events.</p>
              ) : (
                <ScrollArea className="max-h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Tracks</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((evt: any) => (
                        <TableRow key={evt.id}>
                          <TableCell>
                            <div>
                              <span className="font-medium">{evt.series?.series_name || evt.stable_key}</span>
                              {evt.series?.organizer?.display_name && (
                                <p className="text-xs text-muted-foreground">by {evt.series.organizer.display_name}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {evt.start_date ? (
                              <span>
                                {new Date(evt.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {evt.end_date && evt.end_date !== evt.start_date && (
                                  <> – {new Date(evt.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                                )}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">TBD</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {evt.city ? (
                              <span className="inline-flex items-center gap-1 text-sm">
                                <MapPin className="h-3 w-3 text-muted-foreground" />
                                {evt.city}{evt.state ? `, ${evt.state}` : ''}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">TBD</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {evt.tracks?.slice(0, 2).map((t: string) => (
                                <Badge key={t} variant="outline" className="text-xs">{t.replace(/_/g, ' ')}</Badge>
                              ))}
                              {evt.tracks?.length > 2 && <Badge variant="outline" className="text-xs">+{evt.tracks.length - 2}</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Link Artifact"
                                onClick={() => { setShowLinkArtifactDialog(true); }}>
                                <FileText className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Link Offering"
                                onClick={() => { setShowLinkOfferingDialog(true); }}>
                                <Link2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddEditEcosystemEntityDialog
        open={showEntityDialog}
        onOpenChange={(open) => { setShowEntityDialog(open); if (!open) setEditEntity(null); }}
        entity={editEntity}
        defaultParentId={defaultParentId}
      />
      <AddEditEventSeriesDialog
        open={showSeriesDialog}
        onOpenChange={(open) => { setShowSeriesDialog(open); if (!open) setEditSeries(null); }}
        series={editSeries}
        defaultOrganizerId={defaultSeriesOrganizerId}
      />
      <AddEditEventInstanceDialog
        open={showInstanceDialog}
        onOpenChange={setShowInstanceDialog}
      />
      <LinkArtifactToEventDialog
        open={showLinkArtifactDialog}
        onOpenChange={setShowLinkArtifactDialog}
      />
      <LinkOfferingToEventDialog
        open={showLinkOfferingDialog}
        onOpenChange={setShowLinkOfferingDialog}
      />
    </div>
  );
}
